import React, { useState } from "react";
import { useAuth } from "utilities/auth";
import { useToast } from "components/Toast";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import { InfoField } from "components/Account/InfoField";
import { Pencil, Check, X } from "lucide-react";
import {
  INDUSTRY_OTHER,
  buildDbaDiff,
  industryOptions,
} from "utilities/dbaRowEdit";

const EDITABLE_FIELDS = [
  { key: "dbaName", label: "DBA Name", type: "text", required: true },
  { key: "websiteUrl", label: "Website", type: "url", required: true },
  { key: "adminEmail", label: "Admin Email", type: "email", required: true },
  { key: "adminPhone", label: "Admin Phone", type: "tel" },
  {
    key: "customerServiceEmail",
    label: "CS Email",
    type: "email",
    required: true,
  },
  { key: "customerServicePhone", label: "CS Phone", type: "tel" },
  { key: "industryType", label: "Industry", type: "select" },
  // The schema marks these required, but the editor does not: DBAs added after
  // submission were never asked for them, and requiring them here would block
  // fixing a phone number on DBA 2 until someone writes its description.
  {
    key: "businessDescription",
    label: "Description",
    type: "textarea",
    wide: true,
  },
  {
    key: "productsServicesSold",
    label: "Products/Services",
    type: "textarea",
    wide: true,
  },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const URL_RE = /^https?:\/\/.+/i;
const PHONE_RE = /^[+\d][\d\s\-().]{5,}$/;

function validateField(key, value) {
  const def = EDITABLE_FIELDS.find((f) => f.key === key);
  if (!def) return null;
  const trimmed = (value || "").trim();
  if (def.required && !trimmed) return `${def.label} is required`;
  if (!trimmed) return null;
  if (def.type === "email" && !EMAIL_RE.test(trimmed)) {
    return "Enter a valid email";
  }
  if (def.type === "url" && !URL_RE.test(trimmed)) {
    return "Must be a full URL (https://…)";
  }
  if (def.type === "tel" && !PHONE_RE.test(trimmed)) {
    return "Enter a valid phone number";
  }
  return null;
}

/**
 * Row-level inline editor for a single DBA entry. Internal and partner users
 * can flip the row into edit mode via the pencil button, tweak the editable
 * fields, and save them with `PUT /applications/:id/dbas/:dbaId`. Fields
 * cleared to empty are sent as `null` to clear server-side.
 *
 * Partner T&C gate is handled by the caller via `onEditAttempt` — it returns
 * `true` when editing is allowed, `false` when the caller needs to intercept
 * (e.g. open the T&C modal and retry).
 */
export default function DbaRowEditor({
  applicationId,
  dba,
  schema,
  canEdit,
  onEditAttempt,
  onSaved,
}) {
  const auth = useAuth();
  const effectiveCompanyId = useEffectiveCompanyId();
  const { toast } = useToast();

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => snapshotFields(dba));
  const [fieldErrors, setFieldErrors] = useState({});

  const resetFromDba = () => {
    setForm(snapshotFields(dba));
    setFieldErrors({});
  };

  const handleEditClick = () => {
    if (typeof onEditAttempt === "function") {
      const allowed = onEditAttempt();
      if (!allowed) return;
    }
    resetFromDba();
    setEditing(true);
  };

  const handleCancel = () => {
    if (saving) return;
    resetFromDba();
    setEditing(false);
  };

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (fieldErrors[key]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const handleSave = async () => {
    const diff = buildDbaDiff(
      dba,
      form,
      EDITABLE_FIELDS.map((f) => f.key)
    );

    // Validate only what changed, like the API: a stored phone in an odd
    // format must not block an unrelated edit to the same DBA.
    const errors = {};
    for (const key of Object.keys(diff)) {
      const err = validateField(key, form[key]);
      if (err) errors[key] = err;
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;
    if (Object.keys(diff).length === 0) {
      setEditing(false);
      return;
    }

    setSaving(true);
    try {
      await makeApiRequest(
        "put",
        apiRoutes.updateApplicationDba(applicationId, dba.dbaId),
        auth.user,
        effectiveCompanyId,
        diff
      );
      toast({
        status: "success",
        title: "DBA updated",
        description: `${form.dbaName?.trim() || dba.dbaName || "DBA"} updated.`,
      });
      setEditing(false);
      onSaved?.();
    } catch (err) {
      const status = err.response?.status;
      const serverError = err.response?.data?.error;
      console.error("[DbaRowEditor] Save failed", err);
      toast({
        status: "destructive",
        title: "Failed to update DBA",
        description: serverError || `HTTP ${status || "error"}`,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      e.preventDefault();
      handleCancel();
    }
  };

  if (!editing) {
    return (
      <>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-foreground">
            {dba.dbaName || "DBA"}
          </h3>
          {canEdit && dba.dbaId && (
            <button
              type="button"
              className="btn btn-ghost h-7 px-2 text-xs"
              onClick={handleEditClick}
              title="Edit DBA details"
            >
              <Pencil className="w-3.5 h-3.5" />
              <span className="ml-1">Edit</span>
            </button>
          )}
        </div>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
          <InfoField label="DBA Name" value={dba.dbaName} />
          <InfoField label="Website" value={dba.websiteUrl} />
          <InfoField label="Admin Email" value={dba.adminEmail} />
          <InfoField label="Admin Phone" value={dba.adminPhone} />
          <InfoField label="CS Email" value={dba.customerServiceEmail} />
          <InfoField label="CS Phone" value={dba.customerServicePhone} />
          <InfoField label="Industry" value={industryLabel(dba)} />
          {/* Always rendered, blanks included (SOUND-643): these were hidden
              when empty, so a DBA added post-submit showed no slot to fill. */}
          <InfoField
            label="Description"
            value={dba.businessDescription}
            className="md:col-span-2"
          />
          <InfoField
            label="Products/Services"
            value={dba.productsServicesSold}
            className="md:col-span-2"
          />
        </dl>
      </>
    );
  }

  const industryChoices = industryOptions(schema, dba.industryType);

  const inputBase =
    "w-full px-3 py-2 rounded-md bg-card border border-border text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand text-sm";

  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-foreground">
          {form.dbaName || dba.dbaName || "DBA"}
          <span className="ml-2 text-xs font-normal text-brand bg-brand/10 px-2 py-0.5 rounded">
            Editing
          </span>
        </h3>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="btn btn-ghost h-7 px-2 text-xs"
            onClick={handleCancel}
            disabled={saving}
          >
            <X className="w-3.5 h-3.5" />
            <span className="ml-1">Cancel</span>
          </button>
          <button
            type="button"
            className="btn btn-primary h-7 px-2 text-xs"
            onClick={handleSave}
            disabled={saving}
          >
            <Check className="w-3.5 h-3.5" />
            <span className="ml-1">{saving ? "Saving…" : "Save"}</span>
          </button>
        </div>
      </div>
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
        {EDITABLE_FIELDS.map((def) => (
          <div key={def.key} className={def.wide ? "md:col-span-2" : ""}>
            <dt className="text-sm font-medium text-foreground mb-1">
              {def.label}
              {def.required && (
                <span className="text-destructive ml-1">*</span>
              )}
            </dt>
            <dd>
              {def.type === "select" ? (
                <>
                  <select
                    className={inputBase}
                    value={form[def.key] || ""}
                    onChange={(e) => setField(def.key, e.target.value)}
                    disabled={saving}
                  >
                    <option value="">—</option>
                    {industryChoices.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                  {form.industryType === INDUSTRY_OTHER && (
                    <input
                      type="text"
                      className={`${inputBase} mt-2`}
                      placeholder="Describe the industry"
                      value={form.industryTypeOther || ""}
                      onChange={(e) =>
                        setField("industryTypeOther", e.target.value)
                      }
                      onKeyDown={handleKeyDown}
                      disabled={saving}
                    />
                  )}
                </>
              ) : def.type === "textarea" ? (
                <textarea
                  rows={3}
                  className={inputBase}
                  value={form[def.key] || ""}
                  onChange={(e) => setField(def.key, e.target.value)}
                  onKeyDown={(e) => {
                    // Enter makes a newline here; only Escape is a shortcut.
                    if (e.key === "Escape") {
                      e.preventDefault();
                      handleCancel();
                    }
                  }}
                  disabled={saving}
                />
              ) : (
                <input
                  type={def.type}
                  className={`${inputBase} ${
                    fieldErrors[def.key]
                      ? "border-destructive ring-destructive"
                      : ""
                  }`}
                  value={form[def.key] || ""}
                  onChange={(e) => setField(def.key, e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={saving}
                />
              )}
              {fieldErrors[def.key] && (
                <p className="text-xs text-destructive mt-1">
                  {fieldErrors[def.key]}
                </p>
              )}
            </dd>
          </div>
        ))}
      </dl>
    </>
  );
}

function snapshotFields(dba) {
  const snap = { industryTypeOther: dba?.industryTypeOther ?? "" };
  for (const def of EDITABLE_FIELDS) {
    snap[def.key] = dba?.[def.key] ?? "";
  }
  return snap;
}

function industryLabel(dba) {
  if (dba?.industryType === INDUSTRY_OTHER && dba?.industryTypeOther) {
    return `Other — ${dba.industryTypeOther}`;
  }
  return dba?.industryType;
}
